import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toolsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Wrench, Loader2, User, Clock, ArrowLeftRight, History, Search, CheckCircle, AlertCircle, Settings, Trash2, ChevronDown, RotateCcw, Hammer, Download, Upload, FileSpreadsheet, X, DollarSign, Image as ImageIcon, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ListPagination } from '../components/ListPagination';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import { KebabMenu } from '../components/KebabMenu';
import { ImageThumbnail } from '../components/ImageThumbnail';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    available: { label: 'Available', variant: 'success', icon: CheckCircle },
    checked_out: { label: 'Checked Out', variant: 'warning', icon: ArrowLeftRight },
    maintenance: { label: 'Maintenance', variant: 'secondary', icon: Settings },
    retired: { label: 'Retired', variant: 'destructive', icon: AlertCircle },
  };
  
  const config = statusConfig[status] || { label: status, variant: 'outline' };
  const Icon = config.icon;
  
  const variantClasses = {
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-200',
    secondary: 'bg-slate-100 text-slate-800 border-slate-200',
    destructive: 'bg-red-100 text-red-800 border-red-200',
    outline: 'bg-muted text-muted-foreground',
  };
  
  return (
    <Badge className={`${variantClasses[config.variant]} flex items-center gap-1`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.label}
    </Badge>
  );
};

// Repair badge component - shown when tool is checked out for repair
const RepairBadge = () => (
  <Badge className="bg-purple-100 text-purple-800 border-purple-200 flex items-center gap-1">
    <Hammer className="w-3 h-3" />
    Repair
  </Badge>
);

// Repair Needed badge - shown ONLY after failed repair check-in (system-controlled)
const RepairNeededBadge = () => (
  <Badge className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1">
    <AlertCircle className="w-3 h-3" />
    Repair Needed
  </Badge>
);

// Tool card component - mobile optimized
const CONDITION_CONFIG = {
  new:          { label: 'New',          cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  good:         { label: 'Good',         cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  fair:         { label: 'Fair',         cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  needs_repair: { label: 'Needs Repair', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  retired:      { label: 'Retired',      cls: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
};

const ConditionBadge = ({ condition }) => {
  const cfg = CONDITION_CONFIG[condition];
  if (!cfg) return null;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cfg.cls}`}>{cfg.label}</span>;
};

const ToolCard = ({ tool, onCheckout, onReturn, onEdit, onDelete, onViewHistory, isOwner }) => {
  const isCheckedOut = tool.status === 'checked_out';
  const isAvailable = tool.status === 'available';
  const isRepairCheckout = isCheckedOut && tool.checkout_purpose === 'repair';
  // Show "Repair Needed" badge ONLY when tool needs repair (set by failed repair check-in)
  // Never show both badges at the same time - "Repair" is for active checkout, "Repair Needed" is for returned tools
  const showRepairNeeded = !isCheckedOut && tool.needs_repair;
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-3 md:p-4">
        {/* Mobile: stacked layout, Desktop: row layout */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
          {/* Tool info */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <ImageThumbnail
              src={tool.image_thumb_url}
              fullSrc={tool.image_url || tool.image_thumb_url}
              alt={tool.name}
              size="md"
              fallbackIcon={Wrench}
              data-testid={`tool-thumb-${tool.id}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-foreground truncate">{tool.name}</h3>
                <StatusBadge status={tool.status} />
                {isRepairCheckout && <RepairBadge />}
                {showRepairNeeded && <RepairNeededBadge />}
              </div>
              {tool.serial_number && (
                <p className="text-xs text-muted-foreground mt-0.5">S/N: {tool.serial_number}</p>
              )}
              {/* Category + Condition + Value metadata line */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                {tool.category_name && (
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium" data-testid={`tool-category-${tool.id}`}>
                    {tool.category_name}
                  </span>
                )}
                {tool.condition && (
                  <ConditionBadge condition={tool.condition} />
                )}
                {tool.tool_value != null && (
                  <span data-testid={`tool-value-${tool.id}`}>${Number(tool.tool_value).toFixed(2)}</span>
                )}
                {tool.manufacturer && <span>{tool.manufacturer}{tool.model_number ? ` ${tool.model_number}` : ''}</span>}
              </div>
              {tool.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 hidden md:block">{tool.description}</p>
              )}
              
              {/* Current holder info - shows clearly who has it */}
              {isCheckedOut && tool.current_holder_name && (
                <div className={`mt-2 flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm px-2 py-1 rounded-md w-fit ${isRepairCheckout ? 'bg-purple-50' : 'bg-amber-50'}`}>
                  <User className={`w-3 h-3 md:w-4 md:h-4 shrink-0 ${isRepairCheckout ? 'text-purple-600' : 'text-amber-600'}`} />
                  <span className={`font-medium ${isRepairCheckout ? 'text-purple-700' : 'text-amber-700'}`}>{tool.current_holder_name}</span>
                  {tool.last_checkout?.checked_out_at && (
                    <span className={isRepairCheckout ? 'text-purple-600/70' : 'text-amber-600/70'}>
                      · {formatDistanceToNow(new Date(tool.last_checkout.checked_out_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Action buttons - horizontal row on mobile, column on desktop */}
          <div className="flex flex-row md:flex-col items-center md:items-stretch gap-2 shrink-0 border-t md:border-t-0 pt-3 md:pt-0">
            {isAvailable && (
              <Button
                size="sm"
                className="btn-press flex-1 md:flex-initial"
                onClick={() => onCheckout(tool)}
                data-testid={`checkout-btn-${tool.id}`}
              >
                Check Out
              </Button>
            )}
            {isCheckedOut && (
              <Button
                size="sm"
                variant="outline"
                className="btn-press flex-1 md:flex-initial"
                onClick={() => onReturn(tool)}
                data-testid={`return-btn-${tool.id}`}
              >
                Return
              </Button>
            )}
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={() => onViewHistory(tool)}
                title="View History"
              >
                <History className="w-4 h-4" />
              </Button>
              {isOwner && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    onClick={() => onEdit(tool)}
                    title="Edit"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    onClick={() => onDelete(tool)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Tool Activity Feed Component (Collapsible)
const ToolActivityFeed = ({ tools }) => {
  // Default collapsed on all screen sizes (per requirement)
  const [isOpen, setIsOpen] = useState(false);
  
  // Build activity list from tools' last_checkout data
  const recentActivity = useMemo(() => {
    const activities = [];
    
    tools.forEach(tool => {
      // Add checkout events for currently checked out tools
      if (tool.status === 'checked_out' && tool.last_checkout?.checked_out_at) {
        activities.push({
          id: `checkout-${tool.id}`,
          type: 'checked_out',
          toolName: tool.name,
          userName: tool.current_holder_name || 'Unknown',
          timestamp: new Date(tool.last_checkout.checked_out_at),
          notes: tool.last_checkout.notes
        });
      }
      
      // Add return events for tools that were returned (have returned_at in last_checkout)
      if (tool.last_checkout?.returned_at) {
        activities.push({
          id: `return-${tool.id}`,
          type: 'returned',
          toolName: tool.name,
          userName: tool.last_checkout.user_name || 'Unknown',
          timestamp: new Date(tool.last_checkout.returned_at),
          notes: tool.last_checkout.return_notes
        });
      }
    });
    
    // Sort by timestamp descending (most recent first)
    return activities.sort((a, b) => b.timestamp - a.timestamp).slice(0, 10);
  }, [tools]);
  
  // Get last activity hint for collapsed header
  const getLastActivityHint = () => {
    if (recentActivity.length === 0) return null;
    const last = recentActivity[0];
    const action = last.type === 'checked_out' ? 'checked out to' : 'returned by';
    const timeAgo = formatDistanceToNow(last.timestamp, { addSuffix: true });
    return `${last.toolName} ${action} ${last.userName} · ${timeAgo}`;
  };
  
  const lastActivityHint = getLastActivityHint();
  
  // Activity type config
  const activityConfig = {
    checked_out: { 
      label: 'Checked out to', 
      icon: ArrowLeftRight, 
      colorClass: 'text-amber-600 bg-amber-50' 
    },
    returned: { 
      label: 'Returned by', 
      icon: RotateCcw, 
      colorClass: 'text-green-600 bg-green-50' 
    }
  };
  
  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full text-left group">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg whitespace-nowrap">
                <History className="w-4 h-4 md:w-5 md:h-5 shrink-0" />
                <span>Activity</span>
                {!isOpen && (
                  <span className="text-xs md:text-sm font-normal text-muted-foreground ml-1 truncate max-w-[150px] md:max-w-[300px]">
                    {lastActivityHint ? `(${lastActivityHint})` : ''}
                  </span>
                )}
              </CardTitle>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 shrink-0 ${isOpen ? '' : '-rotate-90'}`} />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No recent tool activity</p>
                <p className="text-sm">Tool checkouts and returns will appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentActivity.map(activity => {
                  const config = activityConfig[activity.type];
                  const Icon = config.icon;
                  
                  return (
                    <div
                      key={activity.id}
                      className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${config.colorClass}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium truncate">{activity.toolName}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 text-sm text-muted-foreground">
                              <span>{config.label}</span>
                              <span className="font-medium text-foreground">{activity.userName}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                          <Clock className="w-3 h-3" />
                          <span>{formatDistanceToNow(activity.timestamp, { addSuffix: true })}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export const Tools = () => {
  const { isOwner, isOffice, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageTools = isOwner || isOffice;
  
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Phase 3.5: URL-synced filter and pagination state
  const activeTab = searchParams.get('status') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('per_page')) || 25;
  
  // Local search input for debouncing
  const [searchInput, setSearchInput] = useState(searchQuery);
  
  // Server response pagination data
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Helper to update URL params
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || value === 'all' || (key === 'page' && value === 1) || (key === 'per_page' && value === 25)) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // URL-synced setters
  const setActiveTab = useCallback((value) => {
    updateParams({ status: value, page: 1 });
  }, [updateParams]);
  
  const setSearchQuery = useCallback((value) => {
    updateParams({ search: value, page: 1 });
  }, [updateParams]);
  
  const setPage = useCallback((value) => {
    updateParams({ page: value });
  }, [updateParams]);
  
  const setPageSize = useCallback((value) => {
    updateParams({ per_page: value, page: 1 });
  }, [updateParams]);
  
  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', serial_number: '', manufacturer: '', model_number: '', tool_value: '', tool_category_id: '', condition: '' });
  const [saving, setSaving] = useState(false);
  
  // Checkout dialog
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutTool, setCheckoutTool] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [checkoutNotes, setCheckoutNotes] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);
  const [forRepair, setForRepair] = useState(false);  // Repair checkout checkbox
  
  // Return dialog
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnTool, setReturnTool] = useState(null);
  const [returnNotes, setReturnNotes] = useState('');
  const [returning, setReturning] = useState(false);
  
  // Repair result prompt (shown when returning a repair checkout)
  const [repairResultDialogOpen, setRepairResultDialogOpen] = useState(false);
  const [pendingRepairResult, setPendingRepairResult] = useState(null);  // 'success' or 'failed'
  const [repairCost, setRepairCost] = useState('');  // Phase 2: Optional repair cost
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyTool, setHistoryTool] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTool, setDeleteTool] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  
  // Image upload for add/edit form
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // blob URL or existing URL
  const [removingImage, setRemovingImage] = useState(false);
  const imageInputRef = useRef(null);
  
  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      // Phase 3.5: Server-side pagination and search
      const params = {
        page: page,
        page_size: pageSize,
      };
      
      // Map tab to status filter
      const statusMap = {
        'available': 'available',
        'checked_out': 'checked_out',
        'maintenance': 'maintenance',
      };
      if (activeTab !== 'all' && statusMap[activeTab]) {
        params.status = statusMap[activeTab];
      }
      
      // Add search if present
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const [toolsRes, usersRes, catRes] = await Promise.all([
        toolsAPI.list(params),
        usersAPI.list(),
        toolsAPI.listCategories(),
      ]);
      
      // Handle paginated response
      const toolsData = toolsRes.data;
      setTools(toolsData.items || toolsData);
      setTotal(toolsData.total || (toolsData.items ? toolsData.items.length : toolsData.length));
      setTotalPages(toolsData.total_pages || 1);
      setUsers(usersRes.data.filter(u => u.is_active && !u.archived_at));
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, activeTab, searchQuery]);
  
  // Phase 3.5: Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, setSearchQuery]);
  
  // Phase 3.5: Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter tools based on active tab and search
  // Phase 3.5: Search filtering is now server-side
  const filteredTools = tools.filter(tool => {
    // Tab-specific client-side filtering for maintenance (includes retired)
    if (activeTab === 'maintenance') {
      return tool.status === 'maintenance' || tool.status === 'retired';
    }
    // Other filtering is now server-side
    return true;
  });

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Build payload — strip empty strings to null for optional fields
      const payload = {
        name: formData.name,
        description: formData.description || null,
        serial_number: formData.serial_number || null,
        manufacturer: formData.manufacturer || null,
        model_number: formData.model_number || null,
        tool_value: formData.tool_value !== '' ? parseFloat(formData.tool_value) : null,
        tool_category_id: formData.tool_category_id || null,
        condition: formData.condition || null,
      };
      
      let savedTool;
      if (editingTool) {
        const res = await toolsAPI.update(editingTool.id, payload);
        savedTool = res.data;
        toast.success('Tool updated');
      } else {
        const res = await toolsAPI.create(payload);
        savedTool = res.data;
        toast.success('Tool created');
      }
      
      // Handle image upload if a new file was selected
      if (imageFile && savedTool?.id) {
        try {
          const fd = new FormData();
          fd.append('file', imageFile);
          await toolsAPI.uploadImage(savedTool.id, fd);
        } catch (imgErr) {
          toast.error('Tool saved but image upload failed');
          console.error('Image upload error:', imgErr);
        }
      }
      
      // Handle image removal (edit mode, user clicked remove, no new file)
      if (removingImage && !imageFile && editingTool?.image_url) {
        try {
          await toolsAPI.deleteImage(editingTool.id);
        } catch (imgErr) {
          console.error('Image delete error:', imgErr);
        }
      }
      
      setDialogOpen(false);
      setEditingTool(null);
      resetFormState();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tool');
    } finally {
      setSaving(false);
    }
  };
  
  const resetFormState = () => {
    setFormData({ name: '', description: '', serial_number: '', manufacturer: '', model_number: '', tool_value: '', tool_category_id: '', condition: '' });
    setImageFile(null);
    setImagePreview(null);
    setRemovingImage(false);
    setNewCategoryName('');
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      toast.error('Please select a technician');
      return;
    }
    setCheckingOut(true);
    try {
      await toolsAPI.checkout(checkoutTool.id, {
        user_id: selectedUserId,
        notes: checkoutNotes || null,
        for_repair: forRepair,
      });
      const selectedUser = users.find(u => u.id === selectedUserId);
      const repairText = forRepair ? ' for repair' : '';
      toast.success(`${checkoutTool.name} checked out to ${selectedUser?.name || 'user'}${repairText}`);
      setCheckoutDialogOpen(false);
      setCheckoutTool(null);
      setSelectedUserId('');
      setCheckoutNotes('');
      setForRepair(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out tool');
    } finally {
      setCheckingOut(false);
    }
  };

  // Handle return button click - check if repair checkout needs prompt
  const handleReturnClick = (tool) => {
    setReturnTool(tool);
    setReturnNotes('');
    
    // If this is a repair checkout, show the repair result prompt first
    if (tool.checkout_purpose === 'repair') {
      setRepairResultDialogOpen(true);
    } else {
      // Normal return - show return dialog
      setReturnDialogOpen(true);
    }
  };

  // Handle repair result selection
  const handleRepairResult = (result) => {
    setPendingRepairResult(result);
    setRepairCost('');  // Reset repair cost
    setRepairResultDialogOpen(false);
    setReturnDialogOpen(true);
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    setReturning(true);
    try {
      const payload = {
        notes: returnNotes || null,
      };
      
      // Include repair_result if this was a repair checkout
      if (returnTool?.checkout_purpose === 'repair' && pendingRepairResult) {
        payload.repair_result = pendingRepairResult;
        
        // Phase 2: Include repair_cost if provided and repair was successful
        if (pendingRepairResult === 'success' && repairCost) {
          const cost = parseFloat(repairCost);
          if (!isNaN(cost) && cost >= 0) {
            payload.repair_cost = cost;
          }
        }
      }
      
      await toolsAPI.returnTool(returnTool.id, payload);
      
      // Build success message based on repair result
      let successMsg = `${returnTool.name} returned`;
      if (returnTool?.checkout_purpose === 'repair' && pendingRepairResult) {
        successMsg += pendingRepairResult === 'success' ? ' (repair successful)' : ' (repair not completed)';
        if (pendingRepairResult === 'success' && repairCost) {
          successMsg += ` - Cost: $${parseFloat(repairCost).toFixed(2)}`;
        }
      }
      toast.success(successMsg);
      
      setReturnDialogOpen(false);
      setReturnTool(null);
      setReturnNotes('');
      setPendingRepairResult(null);
      setRepairCost('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return tool');
    } finally {
      setReturning(false);
    }
  };

  const handleViewHistory = async (tool) => {
    setHistoryTool(tool);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const res = await toolsAPI.getHistory(tool.id, 50);
      setHistory(res.data);
    } catch (error) {
      toast.error('Failed to load history');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await toolsAPI.delete(deleteTool.id);
      toast.success(`${deleteTool.name} deleted`);
      setDeleteDialogOpen(false);
      setDeleteTool(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tool');
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      description: tool.description || '',
      serial_number: tool.serial_number || '',
      manufacturer: tool.manufacturer || '',
      model_number: tool.model_number || '',
      tool_value: tool.tool_value != null ? String(tool.tool_value) : '',
      tool_category_id: tool.tool_category_id || '',
      condition: tool.condition || '',
    });
    setImageFile(null);
    setRemovingImage(false);
    setImagePreview(tool.image_url || null);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingTool(null);
    resetFormState();
    setDialogOpen(true);
  };

  // Image handlers for Add/Edit form
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setImageFile(file);
    setRemovingImage(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemovingImage(true);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // Inline category creation
  const handleCreateCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    setCreatingCategory(true);
    try {
      const res = await toolsAPI.createCategory(trimmed);
      const newCat = res.data;
      setCategories(prev => [...prev, newCat]);
      setFormData(prev => ({ ...prev, tool_category_id: newCat.id }));
      setNewCategoryName('');
      toast.success(`Category "${trimmed}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const openCheckoutDialog = (tool) => {
    setCheckoutTool(tool);
    setSelectedUserId('');
    setCheckoutNotes('');
    setForRepair(false);
    setCheckoutDialogOpen(true);
  };

  const openDeleteDialog = (tool) => {
    setDeleteTool(tool);
    setDeleteDialogOpen(true);
  };

  // Export handler
  const handleExport = async () => {
    try {
      const response = await toolsAPI.exportCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tools_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Tools exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export tools');
    }
  };

  // Import handlers
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportFile(selectedFile);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file');
      return;
    }
    
    setImporting(true);
    try {
      const response = await toolsAPI.importCsv(importFile);
      setImportResult(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(response.data.message);
        fetchData(); // Refresh the tools list
      } else if (response.data.skipped > 0) {
        toast.warning(response.data.message);
      }
    } catch (error) {
      console.error('Import failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to import tools';
      toast.error(errorMsg);
      setImportResult({ error: errorMsg });
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleDownloadTemplate = () => {
    const template = `tool_name,serial_asset_number,description
"Cordless Drill","SN-12345","DeWalt 20V MAX"
"Impact Driver","SN-67890","Milwaukee M18"
"Pipe Wrench 24in","","Heavy duty aluminum"`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tools_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Stats
  const totalTools = tools.length;
  const availableCount = tools.filter(t => t.status === 'available').length;
  const checkedOutCount = tools.filter(t => t.status === 'checked_out').length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20 md:pb-0 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">Tools</h1>
            <p className="text-muted-foreground mt-1 hidden sm:block">Track and manage tool assignments</p>
          </div>
          {canManageTools && (
            <div className="hidden md:flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExport}
                data-testid="export-tools-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setImportModalOpen(true)}
                data-testid="import-tools-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button className="h-9 px-4 font-bold btn-press" onClick={openCreateDialog} data-testid="add-tool-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Tool
              </Button>
            </div>
          )}
        </div>

        {/* Stats Cards - Mobile-optimized */}
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          <Card>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Wrench className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold">{totalTools}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-green-600">{availableCount}</p>
                  <p className="text-xs text-muted-foreground">Available</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <ArrowLeftRight className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-amber-600">{checkedOutCount}</p>
                  <p className="text-xs text-muted-foreground">Out</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tools or serial numbers..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 h-12"
                data-testid="tools-search-input"
              />
              {searchInput && (
                <button
                  onClick={() => { setSearchInput(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
            {canManageTools && (
              <div className="md:hidden">
                <KebabMenu
                  items={[{ label: 'Export', icon: Download, onClick: handleExport, 'data-testid': 'tools-export-kebab' }]}
                  data-testid="tools-kebab-menu"
                  className="h-12 w-12"
                />
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">All ({total})</TabsTrigger>
            <TabsTrigger value="available">Available</TabsTrigger>
            <TabsTrigger value="checked_out">Checked Out</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Tools List */}
        {filteredTools.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Wrench className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No tools found</h3>
              <p className="text-muted-foreground mt-1">
                {searchQuery ? 'Try a different search term' : 'Add your first tool to get started'}
              </p>
              {canManageTools && !searchQuery && (
                <Button className="mt-4" onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tool
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTools.map(tool => (
              <ToolCard
                key={tool.id}
                tool={tool}
                onCheckout={openCheckoutDialog}
                onReturn={handleReturnClick}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
                onViewHistory={handleViewHistory}
                isOwner={isOwner}
              />
            ))}
            
            {/* Phase 3.5: Pagination Controls */}
            <ListPagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
              pageSizeOptions={[15, 25, 50, 100]}
            />
          </div>
        )}

        {/* Tool Activity Feed (Collapsible) */}
        <ToolActivityFeed tools={tools} />

        {/* Create/Edit Tool Drawer */}
        <AppDrawer
          open={dialogOpen}
          onOpenChange={(open) => { if (!open) { setDialogOpen(false); } else { setDialogOpen(true); } }}
          title={editingTool ? 'Edit Tool' : 'Add Tool'}
          description={editingTool ? 'Update tool details' : 'Add a new tool to track'}
          size="md"
          isDirty={!!(formData.name || formData.description || formData.serial_number || formData.manufacturer || imageFile)}
          data-testid="tool-form-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" form="tool-form" disabled={saving} data-testid="tool-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTool ? 'Update' : 'Create'}
              </Button>
            </>
          }
        >
          <form id="tool-form" onSubmit={handleCreateOrUpdate} className="space-y-4">
            {/* Tool Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wide">
                Tool Name *
              </Label>
              <Input
                id="name"
                placeholder="e.g., Cordless Drill"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-12 text-lg"
                data-testid="tool-name-input"
              />
            </div>

            {/* Serial Number */}
            <div className="space-y-2">
              <Label htmlFor="serial_number" className="text-sm font-bold uppercase tracking-wide">
                Serial / Asset Number
              </Label>
              <Input
                id="serial_number"
                placeholder="e.g., SN-12345"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className="h-12 text-lg"
                data-testid="tool-serial-input"
              />
            </div>

            {/* Manufacturer + Model (side by side) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="manufacturer" className="text-sm font-bold uppercase tracking-wide">
                  Manufacturer
                </Label>
                <Input
                  id="manufacturer"
                  placeholder="e.g., DeWalt"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  data-testid="tool-manufacturer-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model_number" className="text-sm font-bold uppercase tracking-wide">
                  Model Number
                </Label>
                <Input
                  id="model_number"
                  placeholder="e.g., DCD771"
                  value={formData.model_number}
                  onChange={(e) => setFormData({ ...formData, model_number: e.target.value })}
                  data-testid="tool-model-input"
                />
              </div>
            </div>

            {/* Value + Condition (side by side) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="tool_value" className="text-sm font-bold uppercase tracking-wide">
                  Value / Cost
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    id="tool_value"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.tool_value}
                    onChange={(e) => setFormData({ ...formData, tool_value: e.target.value })}
                    className="pl-7"
                    data-testid="tool-value-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  Condition
                </Label>
                <Select
                  value={formData.condition}
                  onValueChange={(v) => setFormData({ ...formData, condition: v })}
                >
                  <SelectTrigger data-testid="tool-condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="needs_repair">Needs Repair</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">
                Category
              </Label>
              <Select
                value={formData.tool_category_id}
                onValueChange={(v) => setFormData({ ...formData, tool_category_id: v })}
              >
                <SelectTrigger data-testid="tool-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                  {categories.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No categories yet</div>
                  )}
                </SelectContent>
              </Select>
              {/* Inline new category */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="New category name..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="tool-new-category-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); } }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 shrink-0"
                  disabled={!newCategoryName.trim() || creatingCategory}
                  onClick={handleCreateCategory}
                  data-testid="tool-create-category-btn"
                >
                  {creatingCategory ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                </Button>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-bold uppercase tracking-wide">
                Description
              </Label>
              <Textarea
                id="description"
                placeholder="Optional description..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="tool-description-input"
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">
                Tool Image
              </Label>
              {imagePreview && !removingImage ? (
                <div className="relative inline-block">
                  <div className="w-32 h-32 rounded-lg overflow-hidden border bg-muted/30">
                    <img src={imagePreview} alt="Tool preview" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => imageInputRef.current?.click()}
                      data-testid="tool-replace-image-btn"
                    >
                      Replace
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={handleRemoveImage}
                      data-testid="tool-remove-image-btn"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => imageInputRef.current?.click()}
                  data-testid="tool-image-dropzone"
                >
                  <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload an image</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">PNG, JPG up to 10MB</p>
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="tool-image-file-input"
              />
            </div>
          </form>
        </AppDrawer>

        {/* Checkout Tool Drawer */}
        <AppDrawer
          open={checkoutDialogOpen}
          onOpenChange={setCheckoutDialogOpen}
          title="Check Out Tool"
          description={`Assign ${checkoutTool?.name || 'tool'} to a technician`}
          size="md"
          isDirty={!!(selectedUserId || checkoutNotes)}
          data-testid="checkout-tool-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setCheckoutDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" form="checkout-form" disabled={checkingOut || !selectedUserId} data-testid="checkout-confirm-btn">
                {checkingOut && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {forRepair ? 'Check Out for Repair' : 'Check Out'}
              </Button>
            </>
          }
        >
          <form id="checkout-form" onSubmit={handleCheckout} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">
                Assign To *
              </Label>
              <SearchableSelect
                options={users.map(u => ({ value: u.id, label: `${u.name} (${u.role})` }))}
                value={selectedUserId}
                onValueChange={setSelectedUserId}
                placeholder="Select a technician"
                className="h-12"
                emptyText="No users found."
                data-testid="checkout-user-select"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checkout-notes" className="text-sm font-bold uppercase tracking-wide">
                Notes (Optional)
              </Label>
              <Textarea
                id="checkout-notes"
                placeholder="e.g., For job at 123 Main St"
                value={checkoutNotes}
                onChange={(e) => setCheckoutNotes(e.target.value)}
                rows={2}
                data-testid="checkout-notes-input"
              />
            </div>
            
            {/* Repair checkbox */}
            <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
              <Checkbox
                id="for-repair"
                checked={forRepair}
                onCheckedChange={setForRepair}
                data-testid="checkout-for-repair-checkbox"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="for-repair"
                  className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
                >
                  <Hammer className="w-4 h-4 text-purple-600" />
                  Checked out for repair
                </label>
                <p className="text-xs text-muted-foreground">
                  Use this when the tool is being taken to be repaired. It will remain assigned to the technician for accountability.
                </p>
              </div>
            </div>
          </form>
        </AppDrawer>

        {/* Repair Result Prompt Dialog */}
        <AlertDialog open={repairResultDialogOpen} onOpenChange={setRepairResultDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Hammer className="w-5 h-5 text-purple-600" />
                Was the tool successfully repaired?
              </AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-semibold">{returnTool?.name}</span> was checked out for repair. Please confirm whether the repair was completed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogCancel onClick={() => {
                setRepairResultDialogOpen(false);
                setReturnTool(null);
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleRepairResult('failed')}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="repair-result-no-btn"
              >
                No, not completed
              </AlertDialogAction>
              <AlertDialogAction
                onClick={() => handleRepairResult('success')}
                className="bg-green-600 hover:bg-green-700"
                data-testid="repair-result-yes-btn"
              >
                Yes, successfully repaired
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Return Tool Drawer */}
        <AppDrawer
          open={returnDialogOpen}
          onOpenChange={setReturnDialogOpen}
          title="Return Tool"
          description={`Return ${returnTool?.name || 'tool'} from ${returnTool?.current_holder_name || 'holder'}`}
          size="md"
          isDirty={!!(returnNotes || repairCost)}
          data-testid="return-tool-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setReturnDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" form="return-form" disabled={returning} data-testid="return-confirm-btn">
                {returning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Return Tool
              </Button>
            </>
          }
        >
          {returnTool?.checkout_purpose === 'repair' && pendingRepairResult && (
            <div className={`mb-4 px-3 py-2 rounded text-sm ${pendingRepairResult === 'success' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
              {pendingRepairResult === 'success' ? '✓ Repair successful' : '⚠ Repair not completed'}
            </div>
          )}
          <form id="return-form" onSubmit={handleReturn} className="space-y-4">
            {/* Phase 2: Repair cost input - only show when repair was successful */}
            {returnTool?.checkout_purpose === 'repair' && pendingRepairResult === 'success' && (
              <div className="space-y-2">
                <Label htmlFor="repair-cost" className="text-sm font-bold uppercase tracking-wide">
                  Repair Cost (Optional)
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="repair-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={repairCost}
                    onChange={(e) => setRepairCost(e.target.value)}
                    className="pl-7"
                    data-testid="repair-cost-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter the repair cost for record-keeping (visible to admins only)
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="return-notes" className="text-sm font-bold uppercase tracking-wide">
                Notes (Optional)
              </Label>
              <Textarea
                id="return-notes"
                placeholder="e.g., Tool in good condition"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                rows={2}
                data-testid="return-notes-input"
              />
            </div>
          </form>
        </AppDrawer>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <History className="w-5 h-5" />
                Checkout History: {historyTool?.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                View checkout and return history for this tool
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 max-h-[400px] overflow-y-auto">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No checkout history</p>
              ) : (
                <div className="space-y-3">
                  {history.map(record => (
                    <div key={record.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{record.user_name}</span>
                        </div>
                        <Badge variant={record.returned_at ? 'outline' : 'secondary'}>
                          {record.returned_at ? 'Returned' : 'Active'}
                        </Badge>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Out: {new Date(record.checked_out_at).toLocaleString()}
                        </div>
                        {record.returned_at && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            In: {new Date(record.returned_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      {record.notes && (
                        <p className="mt-2 text-sm bg-muted p-2 rounded">{record.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-destructive">Delete Tool</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <span className="font-semibold">{deleteTool?.name}</span>? 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete} 
                disabled={deleting}
                data-testid="delete-confirm-btn"
              >
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Modal */}
        <Dialog open={importModalOpen} onOpenChange={(open) => !open && closeImportModal()}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Import Tools
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to import tools. Tools with matching serial numbers will be updated; otherwise, new tools will be created.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Template download */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  Need a template? Download the CSV template with the required format.
                </p>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
              
              {/* File upload */}
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  Select CSV File
                </Label>
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-primary" />
                      <span className="font-medium">{importFile.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImportFile(null);
                          setImportResult(null);
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to select a CSV file or drag and drop
                      </p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Import columns info */}
              <div className="text-xs text-muted-foreground p-3 bg-muted/30 rounded-lg">
                <p className="font-medium mb-1">Expected columns:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li><span className="font-semibold">tool_name</span> (required)</li>
                  <li><span className="font-semibold">serial_asset_number</span> (optional - used to match existing tools)</li>
                  <li><span className="font-semibold">description</span> (optional)</li>
                </ul>
                <p className="mt-2 italic">Note: Status and checkout info are not imported. All new tools default to Available.</p>
              </div>
              
              {/* Import result */}
              {importResult && (
                <div className={`p-3 rounded-lg ${importResult.error ? 'bg-destructive/10' : 'bg-green-50'}`}>
                  {importResult.error ? (
                    <p className="text-sm text-destructive">{importResult.error}</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-green-800">{importResult.message}</p>
                      <div className="flex gap-4 text-xs">
                        <span className="text-green-600">Created: {importResult.created}</span>
                        <span className="text-blue-600">Updated: {importResult.updated}</span>
                        {importResult.skipped > 0 && (
                          <span className="text-amber-600">Skipped: {importResult.skipped}</span>
                        )}
                      </div>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="mt-2 p-2 bg-amber-50 rounded text-xs">
                          <p className="font-medium text-amber-800 mb-1">Warnings ({importResult.total_errors}):</p>
                          <ul className="list-disc list-inside text-amber-700 max-h-24 overflow-y-auto">
                            {importResult.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={closeImportModal}>
                {importResult && !importResult.error ? 'Close' : 'Cancel'}
              </Button>
              {(!importResult || importResult.error) && (
                <Button 
                  onClick={handleImport} 
                  disabled={!importFile || importing}
                  data-testid="import-confirm-btn"
                >
                  {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Import Tools
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mobile FAB */}
      {canManageTools && (
        <MobileFABContainer data-testid="tools-fab-container">
          <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
            <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); openCreateDialog(); }}>
              Add Tool
            </MobileFABMenuItem>
            <MobileFABMenuItem icon={Upload} onClick={() => { setFabOpen(false); setImportModalOpen(true); }}>
              Import
            </MobileFABMenuItem>
          </MobileFABMenu>
          <button
            className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
            onClick={() => setFabOpen(!fabOpen)}
            data-testid="tools-mobile-fab"
          >
            <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
          </button>
        </MobileFABContainer>
      )}
    </Layout>
  );
};
